import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbPage,
} from "@/components/admin/breadcrumb";
import { cn } from "@/lib/utils";
import type { CreateBaseProps } from "ra-core";
import {
  CreateBase,
  Translate,
  useCreateContext,
  useCreatePath,
  useGetResourceLabel,
  useHasDashboard,
  useResourceContext,
} from "ra-core";
import type { ReactNode } from "react";
import { Link } from "react-router";

export type CreateProps = CreateViewProps & CreateBaseProps;

/**
 * A complete create page with breadcrumb, title, and actions.
 *
 * Combines data fetching, form context, and UI layout for creating new records. Renders breadcrumb
 * navigation, page title, and wraps your form components.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/create/ Create documentation}
 *
 * @example
 * import { Create, SimpleForm, TextInput } from '@/components/admin';
 *
 * export const PostCreate = () => (
 *   <Create>
 *     <SimpleForm>
 *       <TextInput source="title" />
 *       <TextInput source="body" />
 *     </SimpleForm>
 *   </Create>
 * );
 */
export const Create = ({
  actions,
  children,
  className,
  disableBreadcrumb,
  title,
  ...rest
}: CreateProps) => (
  <CreateBase {...rest}>
    <CreateView
      actions={actions}
      className={className}
      disableBreadcrumb={disableBreadcrumb}
      title={title}
    >
      {children}
    </CreateView>
  </CreateBase>
);

export type CreateViewProps = {
  actions?: ReactNode;
  disableBreadcrumb?: boolean;
  children: ReactNode;
  className?: string;
  title?: ReactNode | string | false;
};

/**
 * The view component for Create pages with layout and UI.
 *
 * @internal
 */
export const CreateView = ({
  actions,
  disableBreadcrumb,
  title,
  children,
  className,
}: CreateViewProps) => {
  const context = useCreateContext();

  const resource = useResourceContext();
  if (!resource) {
    throw new Error(
      "The CreateView component must be used within a ResourceContextProvider",
    );
  }
  const getResourceLabel = useGetResourceLabel();
  const listLabel = getResourceLabel(resource, 2);
  const createPath = useCreatePath();
  const listLink = createPath({
    resource,
    type: "list",
  });
  const hasDashboard = useHasDashboard();

  return (
    <div
      data-slot="edit-view"
      className="relative -top-4 lg:-top-4 -mb-4 w-auto min-h-screen bg-[#f0f4f9] dark:bg-slate-950 -mx-4 lg:-mx-8 px-4 lg:px-8 pb-16 flex flex-col"
    >
      {/* ── FIXED FULL-WIDTH HEADER (STAYS FIXED ON TOP) ── */}
      <div
        className={cn(
          "sticky top-0 z-50 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 py-4 px-4 lg:px-8 -mx-4 lg:-mx-8 mb-6 flex justify-center w-auto shadow-sm",
          className,
        )}
      >
        <div className="max-w-5xl w-full flex justify-between items-center flex-wrap gap-4">
          <div className="text-xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
            {title !== undefined ? title : context.defaultTitle}
          </div>
          <div
            id="ra-form-actions-portal"
            className="flex items-center gap-2 relative z-50"
          >
            {actions}
          </div>
        </div>
      </div>

      {/* ── CONTENT AREA ── */}
      <div className="max-w-5xl w-full mx-auto flex-1 flex flex-col px-2">
        {!disableBreadcrumb && (
          <Breadcrumb className="mb-6">
            {hasDashboard && (
              <BreadcrumbItem>
                <Link to="/">
                  <Translate i18nKey="ra.page.dashboard">Home</Translate>
                </Link>
              </BreadcrumbItem>
            )}
            <BreadcrumbItem>
              <Link to={listLink}>{listLabel}</Link>
            </BreadcrumbItem>
            <BreadcrumbPage>
              <Translate i18nKey="ra.action.create">Create</Translate>
            </BreadcrumbPage>
          </Breadcrumb>
        )}
        <div className="flex-1">{children}</div>
      </div>
    </div>
  );
};
